/**
 * Dashboards API (issue #16) — typed methods over the tenant-aware `api`
 * client. Every call rides the shared Authorization + X-Tenant injection in
 * client.ts; the backend RLS scopes every aggregate to the caller's tenant
 * (and, for the instructor endpoint, to the caller's OWN courses — see
 * backend/app/routers/dashboard.py's module docstring for the RLS
 * under-scope this endpoint compensates for).
 */
import { api } from "@/lib/api/client";
import type { MeetingProvider } from "@/lib/api/tenant";
import type { Modality } from "@/lib/api/courses";
import type { DestinationState } from "@/lib/api/sessionPortal";

export interface AdminKpis {
  current_students: number;
  courses_this_term: number;
  faculty_count: number;
}

export interface FunnelStage {
  stage: string;
  count: number;
}

export interface AdminTerm {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
}

/**
 * One meeting on one date, as a dashboard CARD (issue #321 Milestone 1).
 *
 * `join_url` is deliberately ABSENT, and its absence is the point. The card
 * used to carry it and each dashboard decided for itself whether the meeting
 * was usable; the portal now answers that once, on the server, and the card
 * carries `id` so it can route there instead.
 *
 * `destination_state` is that server derivation. Do not recompute it, and do
 * not add a URL back to this legacy type. RegistrarSession alone carries its
 * ready-only canonical URL for the registrar Home.
 */
export interface TodaySession {
  id: string;
  course_id: string;
  course_code: string;
  title: string;
  session_label: string | null;
  starts_at: string;
  status: SessionStatus;
  /** Whether the meeting is physically in a room. Inherits INDEPENDENTLY of
   *  `meeting_provider`, so it is carried separately from the provider. */
  modality: Modality;
  meeting_provider: MeetingProvider;
  location: string | null;
  destination_state: DestinationState;
}

export interface RegistrarSession extends TodaySession {
  timezone: string;
  meeting_url: string | null;
}

export interface AnnouncementSummary {
  id: string;
  title: string;
  published_at: string | null;
  is_urgent: boolean;
}

export type RegistrarAttentionKind =
  | "identity_recovery"
  | "invitation_provisioning_failed"
  | "invitation_delivery_failed"
  | "invitation_credential_setup"
  | "invitation_expired"
  | "admitted_without_student"
  | "converted_without_invitation"
  | "publication_blocked"
  | "invitation_expiring";

export interface RegistrarAttentionAction {
  label: string;
  href: string;
  reason: string;
}

export interface RegistrarAttentionItem {
  kind: RegistrarAttentionKind;
  record_type: "managed_identity_resolution_issue" | "invitation" | "applicant" | "course";
  record_id: string;
  identity: string;
  evidence: string;
  state: string;
  attention_at: string;
  rank: number;
  action: RegistrarAttentionAction;
}

export interface RegistrarAttention {
  version: "registrar-attention-v1";
  total: number;
  overflow_count: number;
  primary_action: RegistrarAttentionAction | null;
  offered_row_key?: string | null;
  items: RegistrarAttentionItem[];
}

export type AdminSection<T> =
  | { state: "ready"; value: T }
  | { state: "empty" }
  | { state: "unavailable" };

export interface AdminTerms {
  active_terms: AdminTerm[];
  next_term: AdminTerm | null;
}

export interface AdminAdmissions {
  open_applicants: number;
  funnel: FunnelStage[];
}

/** One read section per registrar box. Unavailable carries no substitute zero. */
export interface AdminDashboard {
  projection_version: "admin-home-v2";
  generated_at: string;
  attention: AdminSection<RegistrarAttention>;
  snapshot: AdminSection<AdminKpis>;
  terms: AdminSection<AdminTerms>;
  admissions: AdminSection<AdminAdmissions>;
  sessions: AdminSection<RegistrarSession[]>;
  announcements: AdminSection<AnnouncementSummary[]>;
}

export interface InstructorKpis {
  my_courses: number;
  students_taught: number;
  sessions_this_week: number;
  grades_pending: number;
}

export interface InstructorCourse {
  id: string;
  code: string;
  crn: string | null;
  title: string;
  title_ar: string | null;
  program_name: string | null;
  modality: Modality;
  location: string | null;
  meeting_days: string | null;
  meeting_time: string | null;
  meeting_provider: MeetingProvider;
  join_url: string | null;
  enrolled_count: number;
}

export type TodoKind = "attendance" | "grading";

export interface TodoItem {
  kind: TodoKind;
  label: string;
  course_code: string | null;
}

export interface CourseAnnouncement {
  id: string;
  course_id: string;
  title: string;
  published_at: string | null;
}

export interface InstructorDashboard {
  kpis: InstructorKpis;
  my_courses: InstructorCourse[];
  todo: TodoItem[];
  course_announcements: CourseAnnouncement[];
  today: InstructorTodaySession[];
  needs_attention: AttentionItem[];
  recent_activity: InstructorActivityItem[];
  upcoming: InstructorUpcomingItem[];
  /** Current/next unit projection, one entry per course in `my_courses`. */
  course_sequence: CourseSequence[];
}

export interface InstructorTodaySession {
  id: string;
  course_id: string;
  course_code: string;
  course_title: string;
  session_label: string | null;
  session_date: string;
  starts_at: string;
  meeting_time: string | null;
  modality: Modality;
  location: string | null;
  meeting_provider: MeetingProvider;
  /** Server-derived. `join_url` was here and is gone — see `TodaySession`. */
  destination_state: DestinationState;
  attendance_state: "scheduled" | "held";
  course_url: string;
  attendance_url: string;
  /** The session portal, under `/instructor`; the admin teaching shell rewrites
   *  it with the same `workspacePath` helper the other two links use. */
  session_url: string;
}

export interface AttentionItem {
  repair_address?: `${string}:${string}` | null;
  kind: "review" | "attendance" | "overdue" | "returned" | "absences";
  record_id: string;
  course_id: string;
  course_code: string;
  label: string;
  condition: string;
  occurred_at: string;
  destination: string;
}

export interface InstructorActivityItem {
  kind: "submission" | "resubmission" | "feedback" | "enrollment";
  record_id: string;
  course_id: string;
  course_code: string;
  label: string;
  occurred_at: string;
  destination: string;
}

export interface InstructorUpcomingItem {
  kind: "session" | "assignment";
  record_id: string;
  course_id: string;
  course_code: string;
  label: string;
  at: string;
  destination: string;
}

export interface Welcome {
  preferred_name: string | null;
  honorific: string | null;
  program_name: string | null;
  cohort_name: string | null;
}

export interface Term {
  name: string;
  start_date: string | null;
  end_date: string | null;
}

export interface StudentCourse {
  id: string;
  code: string;
  crn: string | null;
  credits: number | null;
  title: string;
  title_ar: string | null;
  instructor_honorific: string | null;
  instructor_name: string | null;
  meeting_days: string | null;
  meeting_time: string | null;
  modality: Modality;
  meeting_provider: MeetingProvider;
  join_url: string | null;
}

export interface Deadline {
  course_code: string;
  label: string;
  /**
   * The authoritative instant. Sessions use lms_session.starts_at and native
   * coursework uses lms_assignment.due_at.
   */
  at: string;
  kind: "session" | "assignment";
  destination: string;
}

export interface Attendance {
  rate_pct: number;
  present: number;
  late: number;
  absent: number;
  excused: number;
  unexcused_absence_alert: boolean;
}

export interface GradeSummary {
  course_code: string;
  running_pct: number | null;
  letter: string | null;
}

export interface CourseAttendanceProgress {
  rate_pct: number | null;
  present: number;
  late: number;
  absent: number;
  excused: number;
}

export interface ProvisionalProgress {
  status: "current" | "unavailable" | "inconsistent" | "stale";
  basis_id: string | null;
  percentage: number | null;
  letter: string | null;
  advisory: boolean | null;
}

export interface CertifiedProgress {
  status: "certified" | "uncertified";
  letter: string | null;
  score: number | null;
  kind: "certification" | "correction" | null;
  certified_at: string | null;
}

export interface StudentCourseProgress {
  course_id: string;
  course_code: string;
  upcoming_native: number;
  overdue_native: number;
  submitted_count: number;
  returned_count: number;
  graded_count: number;
  attendance: CourseAttendanceProgress;
  provisional: ProvisionalProgress;
  certified: CertifiedProgress;
}

/** MVP lean-cut (PRD §6.9): deliberately NO balance/standing/financial field. */
export interface StudentDashboard {
  welcome: Welcome;
  term: Term | null;
  courses: StudentCourse[];
  /** Today's meetings with RESOLVED destinations — the only place a student
   *  receives the per-session override. `courses` above stays course-facing and
   *  carries the term-wide default, which is the wrong link on any day a session
   *  overrides it. */
  today_sessions: TodaySession[];
  upcoming_deadlines: Deadline[];
  recent_announcements: AnnouncementSummary[];
  attendance: Attendance;
  progress: StudentCourseProgress[];
  grades: GradeSummary[];
  /** Current/next unit projection, one entry per course in `courses`. */
  course_sequence: CourseSequence[];
}

/**
 * Current/next unit projection (Wave 2 T3) — one entry per course on both the
 * student and instructor dashboards. The server resolves which unit is
 * "current" and which is "next"; the browser renders `starts_at` and never
 * recomputes either.
 */
export type CourseSequenceState = "before_course" | "in_progress" | "after_course" | "no_units";

export type SessionStatus = "scheduled" | "held" | "cancelled";

/**
 * The session attached to a teaching unit (issue #321 Milestone 1).
 *
 * `join_url` is absent for the same reason it is absent from `TodaySession`: a
 * sequence summary says WHEN the class is and links to the one place that can
 * act on it. `destination_state` is the server's readiness answer.
 */
export interface CourseSequenceSession {
  session_id: string;
  starts_at: string;
  status: SessionStatus;
  modality: Modality;
  meeting_provider: MeetingProvider;
  location: string | null;
  destination_state: DestinationState;
}

export interface CourseSequenceUnit {
  unit_id: string;
  position: number;
  topic: string;
  preparation_guidance: string | null;
  recap: string | null;
  follow_up: string | null;
  /** `null` means no session is linked to this unit — a different fact from a
   *  linked session whose `status` is `"cancelled"`. */
  session: CourseSequenceSession | null;
}

export interface CourseSequence {
  course_id: string;
  /** `state` carries the fact; `current`/`next` nullness alone does not
   *  distinguish `before_course` from `after_course` (they are exact mirrors
   *  of each other on nullness), so render off `state`. */
  state: CourseSequenceState;
  current: CourseSequenceUnit | null;
  next: CourseSequenceUnit | null;
}

export const dashboardApi = {
  admin: () => api.get<AdminDashboard>("/dashboard/admin"),
  instructor: () => api.get<InstructorDashboard>("/dashboard/instructor"),
  student: () => api.get<StudentDashboard>("/dashboard/student"),
};
